import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { DataTable } from '../../../components/common';
import { fmtCurrency } from '../../../utils/formatNumber';
import { formatDate } from '../../../utils/dateUtils';
import styles from '../MemberReports.module.css';

const columnHelper = createColumnHelper();

const getReceiptMemberName = (receipt, memberOptions = []) => {
  if (receipt.receiptType === 'anonymous') {
    return receipt.donorName || 'Shah A/C';
  }

  if (receipt.member?.name) return receipt.member.name;

  const userId = receipt.userId?.id ?? receipt.userId;
  return memberOptions.find((member) => String(member.id) === String(userId))?.name || 'Unknown / Walk-in';
};

export default function FullInfoReport({
  receipts,
  categories,
  filterYear,
  filterCategoryId,
  filterMemberId,
  memberOptions,
  viewMode = 'detail',
}) {
  const navigate = useNavigate();

  const filteredReceipts = useMemo(() => {
    let result = receipts;
    if (filterYear) {
      result = result.filter(
        (receipt) => String(receipt.financialYearId?.id ?? receipt.financialYearId) === String(filterYear)
      );
    }
    if (filterCategoryId) {
      result = result.filter((receipt) => {
        const categoryId = receipt.donationCategoryId?.id ?? receipt.donationCategoryId ?? receipt.DonationCategory?.id;
        if (String(categoryId) === String(filterCategoryId)) return true;

        if (receipt.pledgeSettlements?.length > 0) {
          return receipt.pledgeSettlements.some((settlement) => {
            const settlementCategoryId = settlement.offering?.donationCategoryId ?? settlement.offering?.DonationCategory?.id;
            return String(settlementCategoryId) === String(filterCategoryId);
          });
        }

        return false;
      });
    }
    if (filterMemberId) {
      result = result.filter((receipt) => String(receipt.userId?.id ?? receipt.userId) === filterMemberId);
    }

    return result.sort((left, right) => (left.receiptNo || '').localeCompare(right.receiptNo || ''));
  }, [receipts, filterYear, filterCategoryId, filterMemberId, memberOptions]);

  const getCategoryAmount = (receipt, categoryId) => {
    if (receipt.pledgeSettlements?.length > 0) {
      const sum = receipt.pledgeSettlements.reduce((total, settlement) => {
        const settlementCategoryId = settlement.Offering?.donationCategoryId ?? settlement.offering?.donationCategoryId;
        if (String(settlementCategoryId) === String(categoryId)) {
          return total + (parseFloat(settlement.settledAmount) || 0);
        }
        return total;
      }, 0);
      if (sum > 0) return sum;
    }

    const receiptCategoryId = receipt.donationCategoryId?.id ?? receipt.donationCategoryId ?? receipt.DonationCategory?.id;
    if (receiptCategoryId && String(receiptCategoryId) === String(categoryId)) {
      return parseFloat(receipt.amountInr) || 0;
    }

    return 0;
  };

  const totalSums = useMemo(() => {
    const sums = { totalInr: 0 };
    categories.forEach((category) => {
      sums[category.id] = 0;
    });

    filteredReceipts.forEach((receipt) => {
      sums.totalInr += parseFloat(receipt.amountInr) || 0;
      categories.forEach((category) => {
        sums[category.id] += getCategoryAmount(receipt, category.id);
      });
    });

    return sums;
  }, [filteredReceipts, categories]);

  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor((_, index) => index + 1, {
        id: '#',
        header: '#',
        cell: (info) => info.getValue(),
        size: 50,
      }),
      columnHelper.accessor('receiptNo', {
        id: 'receiptNo',
        header: 'Receipt No',
        cell: (info) => (
          <button
            className="btn btn-ghost btn-sm"
            style={{ padding: '2px 6px', fontFamily: 'monospace', fontWeight: 600 }}
            onClick={() => navigate(`/accounting/receipts/${info.row.original.id}`)}
            title="View receipt detail"
          >
            {info.getValue()}
          </button>
        ),
        size: 150,
      }),
      columnHelper.accessor('receiptDate', {
        id: 'date',
        header: 'Date',
        cell: (info) => <div style={{ whiteSpace: 'nowrap' }}>{formatDate(info.getValue())}</div>,
        size: 120,
      }),
      columnHelper.accessor((receipt) => getReceiptMemberName(receipt, memberOptions), {
        id: 'member',
        header: 'Member',
        cell: (info) => <div className={styles.nameCell} style={{ minWidth: 150 }}>{info.getValue()}</div>,
        footer: () => <strong style={{ display: 'block', textAlign: 'right' }}>Totals</strong>,
      }),
    ];

    if (viewMode !== 'summary') {
      categories.forEach((category) => {
        cols.push(
          columnHelper.accessor((receipt) => getCategoryAmount(receipt, category.id), {
            id: `cat_${category.id}`,
            header: category.name,
            cell: (info) => {
              const value = info.getValue();
              return value > 0
                ? <div style={{ textAlign: 'right' }}>{fmtCurrency(value)}</div>
                : <div style={{ textAlign: 'center', color: '#ccc' }}>—</div>;
            },
            footer: () => (
              <div style={{ textAlign: 'right' }}>
                <strong>{totalSums[category.id] > 0 ? fmtCurrency(totalSums[category.id]) : '—'}</strong>
              </div>
            ),
            size: Math.max(100, category.name.length * 8 + 30),
          })
        );
      });
    }

    cols.push(
      columnHelper.accessor('amountInr', {
        id: 'total',
        header: 'Total (Rs)',
        cell: (info) => <div style={{ textAlign: 'right', fontWeight: 600 }}>{fmtCurrency(info.getValue())}</div>,
        footer: () => <div style={{ textAlign: 'right' }}><strong>{fmtCurrency(totalSums.totalInr)}</strong></div>,
        size: 110,
      }),
      columnHelper.accessor('manualReceiptNo', {
        id: 'manualReceiptNo',
        header: 'Manual Rec No',
        cell: (info) => info.getValue() || '—',
        size: 130,
      })
    );

    return cols;
  }, [categories, memberOptions, totalSums, viewMode, navigate]);

  return (
    <div className={styles.reportContainer} style={{ overflowX: 'auto' }}>
      <DataTable
        data={filteredReceipts}
        columns={columns}
        emptyStateMessage="No receipts match the given filters."
      />
    </div>
  );
}
